import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { ColumnDef } from "@tanstack/react-table";
import { useState, type FormEvent } from "react";
import { useOutletContext } from "react-router-dom";
import { adminApi } from "../../api/admin";
import type { AdminPaymentRow } from "../../api/types";
import { errorMessage } from "../../shared/errors";
import {
  formatCredits,
  formatDateTime,
  formatMoney,
  utcTooltip,
} from "../../shared/format";
import { roleAtLeast, type StaffRole } from "../../shared/hooks/useStaff";
import {
  Alert,
  Badge,
  Button,
  Card,
  DataTable,
  Input,
  Modal,
  Spinner,
} from "../../ui";
import {
  Pager,
  Select,
  StaffAction,
  stateTone,
  useCursorPages,
} from "./shared";

const columns: ColumnDef<AdminPaymentRow, unknown>[] = [
  {
    id: "created_at",
    header: "Created",
    cell: ({ row }) => (
      <span title={utcTooltip(row.original.created_at)}>
        {formatDateTime(row.original.created_at)}
      </span>
    ),
  },
  { accessorKey: "product", header: "Product" },
  { accessorKey: "provider", header: "Provider" },
  {
    id: "amount",
    header: "Amount",
    cell: ({ row }) => formatMoney(row.original.amount, row.original.currency),
  },
  {
    id: "credits",
    header: "Credits",
    cell: ({ row }) => formatCredits(row.original.credits),
  },
  {
    id: "state",
    header: "State",
    cell: ({ row }) => (
      <Badge tone={stateTone(row.original.state)}>{row.original.state}</Badge>
    ),
  },
  {
    id: "org",
    header: "Org",
    cell: ({ row }) => (
      <span className="text-xs text-slate-500">{row.original.org_id}</span>
    ),
  },
];

const EMPTY = {
  q: "",
  state: "",
  provider: "",
  product: "",
  from: "",
  to: "",
};

export function PaymentsPage() {
  const { role, product } = useOutletContext<{
    role: StaffRole;
    product: string | null;
  }>();
  const canMutate = roleAtLeast(role, "admin");
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState(EMPTY);
  const [filters, setFilters] = useState(EMPTY);
  const pages = useCursorPages();
  const [selected, setSelected] = useState<string | null>(null);
  const [refund, setRefund] = useState<{
    amount: string;
    reason: string;
  } | null>(null);
  const [message, setMessage] = useState<{
    kind: "success" | "error";
    text: string;
  } | null>(null);
  const list = useQuery({
    queryKey: ["admin", "payments", product, filters, pages.cursor],
    queryFn: () =>
      adminApi.payments({
        ...filters,
        product: product ?? filters.product,
        cursor: pages.cursor,
      }),
  });
  const detail = useQuery({
    queryKey: ["admin", "payment", selected],
    enabled: !!selected,
    queryFn: () => adminApi.payment(selected ?? ""),
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    setFilters(draft);
    pages.reset();
  };
  const doRefund = () => {
    if (!selected || !refund) return;
    adminApi
      .refund(selected, {
        amount: Math.round(Number(refund.amount) * 100),
        reason: refund.reason,
      })
      .then((r) => {
        setMessage({
          kind: "success",
          text: `Refund ${r.refund_ref} requested.`,
        });
        setRefund(null);
        return queryClient.invalidateQueries({
          queryKey: ["admin", "payment"],
        });
      })
      .catch((err) => {
        setMessage({ kind: "error", text: errorMessage(err) });
        setRefund(null);
      });
  };
  const p = detail.data;
  return (
    <div className="flex flex-col gap-4">
      <Card>
        <h1 className="mb-4 text-xl font-semibold">Payments</h1>
        <form
          onSubmit={submit}
          className="mb-4 grid gap-3 md:grid-cols-4"
          aria-label="Filter payments"
        >
          <Input
            label="Search"
            placeholder="payment id, provider ref, org id, customer"
            value={draft.q}
            onChange={(e) => setDraft({ ...draft, q: e.target.value })}
          />
          <Select
            label="State"
            value={draft.state}
            onChange={(state) => setDraft({ ...draft, state })}
            options={[
              { value: "", label: "any" },
              ...["pending", "succeeded", "failed", "refunded", "disputed"].map(
                (s) => ({ value: s, label: s }),
              ),
            ]}
          />
          <Select
            label="Provider"
            value={draft.provider}
            onChange={(provider) => setDraft({ ...draft, provider })}
            options={[
              { value: "", label: "any" },
              { value: "stripe", label: "stripe" },
              { value: "pi_network", label: "pi_network" },
            ]}
          />
          <Input
            label="From"
            type="date"
            value={draft.from}
            onChange={(e) => setDraft({ ...draft, from: e.target.value })}
          />
          <Input
            label="To"
            type="date"
            value={draft.to}
            onChange={(e) => setDraft({ ...draft, to: e.target.value })}
          />
          <div className="flex items-end">
            <Button type="submit">Filter</Button>
          </div>
        </form>
        {message ? <Alert kind={message.kind}>{message.text}</Alert> : null}
        {list.isLoading ? <Spinner /> : null}
        {list.data ? (
          <DataTable<AdminPaymentRow>
            columns={columns}
            data={list.data.items}
            onRowClick={(row) => setSelected(row.id)}
            emptyMessage="No payments match."
          />
        ) : null}
        <Pager pages={pages} nextCursor={list.data?.next_cursor} />
      </Card>
      {selected ? (
        <Card>
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold">Payment {selected}</h2>
            <Button variant="secondary" onClick={() => setSelected(null)}>
              Close
            </Button>
          </div>
          {detail.isLoading ? <Spinner /> : null}
          {p ? (
            <div className="mt-3 grid gap-4 md:grid-cols-2">
              <dl className="text-sm">
                <dt className="font-medium">State</dt>
                <dd>
                  <Badge tone={stateTone(p.state)}>{p.state}</Badge>
                </dd>
                <dt className="mt-2 font-medium">Amount</dt>
                <dd>
                  {formatMoney(p.amount, p.currency)} · refunded{" "}
                  {formatMoney(p.refunded_amount, p.currency)}
                </dd>
                <dt className="mt-2 font-medium">Credits</dt>
                <dd>{formatCredits(p.credits)}</dd>
                <dt className="mt-2 font-medium">Provider refs</dt>
                <dd className="text-xs text-slate-600">
                  {[
                    p.provider_ref,
                    p.provider_intent_ref,
                    p.provider_charge_ref,
                  ]
                    .filter(Boolean)
                    .join(" · ") || "—"}
                </dd>
                <dt className="mt-2 font-medium">Receipt</dt>
                <dd>
                  {p.receipt ? (
                    p.receipt.download_url ? (
                      <a
                        className="text-brand underline"
                        href={p.receipt.download_url}
                      >
                        {p.receipt.receipt_number}
                      </a>
                    ) : (
                      p.receipt.receipt_number
                    )
                  ) : (
                    "—"
                  )}
                </dd>
                {p.failure_reason ? (
                  <>
                    <dt className="mt-2 font-medium">Failure</dt>
                    <dd>{p.failure_reason}</dd>
                  </>
                ) : null}
              </dl>
              <div>
                <h3 className="text-sm font-medium">Provider events</h3>
                <ul className="mt-1 text-xs text-slate-600">
                  {p.provider_events.length === 0 ? <li>None</li> : null}
                  {p.provider_events.map((e) => (
                    <li key={e.event_id}>
                      {formatDateTime(e.received_at)} ·{" "}
                      <code>{e.event_type}</code>
                      {e.error ? ` · error: ${e.error}` : ""}
                    </li>
                  ))}
                </ul>
                <div className="mt-4">
                  <StaffAction
                    allowed={
                      canMutate &&
                      p.provider === "stripe" &&
                      (p.state === "succeeded" || p.state === "refunded")
                    }
                    title={
                      canMutate
                        ? "Only succeeded Stripe payments can be refunded"
                        : "Read-only role"
                    }
                    variant="danger"
                    onClick={() =>
                      setRefund({
                        amount: ((p.amount - p.refunded_amount) / 100).toFixed(
                          2,
                        ),
                        reason: "",
                      })
                    }
                  >
                    Refund
                  </StaffAction>
                </div>
              </div>
            </div>
          ) : null}
        </Card>
      ) : null}
      <Modal
        open={refund !== null}
        title="Refund payment"
        onClose={() => setRefund(null)}
        footer={
          <>
            <Button variant="secondary" onClick={() => setRefund(null)}>
              Cancel
            </Button>
            <Button
              variant="danger"
              disabled={!refund?.reason || !refund?.amount}
              onClick={doRefund}
            >
              Confirm
            </Button>
          </>
        }
      >
        <Input
          label="Amount"
          type="number"
          step="0.01"
          value={refund?.amount ?? ""}
          onChange={(e) =>
            setRefund({ ...(refund ?? { reason: "" }), amount: e.target.value })
          }
        />
        <div className="mt-2">
          <Input
            label="Reason"
            value={refund?.reason ?? ""}
            onChange={(e) =>
              setRefund({
                ...(refund ?? { amount: "" }),
                reason: e.target.value,
              })
            }
          />
        </div>
      </Modal>
    </div>
  );
}
